import { useId, useState } from 'react';
import type { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { improvementSchema, improvementStatusLabels, type Improvement, type ImprovementInput } from '@hjci-investorhub-v2/shared';
import { patch, post } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Dialog } from '../../components/ui/dialog';
import { ErrorNotice, Field } from '../../components/ui/common';
import { useUnsavedChanges } from '../../components/unsaved-changes';
import { ImprovementMarkdown } from './improvement-markdown';

export function ImprovementForm({ item, onClose, onSaved }: {
  item?: Improvement; onClose: () => void; onSaved: (item: Improvement) => void;
}) {
  const id = useId();
  const query = useQueryClient();
  const [preview, setPreview] = useState(false);
  const form = useForm<z.input<typeof improvementSchema>, unknown, ImprovementInput>({ resolver: zodResolver(improvementSchema), defaultValues: {
    title: item?.title ?? '', description: item?.description ?? '', status: item?.status ?? 'open',
  } });
  const confirmClose = useUnsavedChanges(form.formState.isDirty);
  const save = useMutation({
    mutationFn: (data: ImprovementInput) => item ? patch<Improvement>(`/improvements/${item.id}`, data) : post<Improvement>('/improvements', data),
    onSuccess: async saved => {
      await query.invalidateQueries({ queryKey: ['/improvements'] });
      onSaved(saved);
    },
  });
  const close = () => { if (!save.isPending) confirmClose(onClose); };
  const description = form.watch('description') ?? '';
  return <Dialog open onOpenChange={open => { if (!open) close(); }} title={item ? 'Edit improvement' : 'Add improvement'} description="Record an idea and any conditions that need to be met before it can go ahead.">
    <form onSubmit={form.handleSubmit(values => save.mutate(values))}>
      <fieldset disabled={save.isPending} className="improvement-fields form-stack">
        <Field label="Title" htmlFor={`${id}-title`} error={form.formState.errors.title?.message}>
          <input id={`${id}-title`} maxLength={160} required {...form.register('title')} />
        </Field>
        <Field label="Status" htmlFor={`${id}-status`} error={form.formState.errors.status?.message}>
          <select id={`${id}-status`} {...form.register('status')}>{Object.entries(improvementStatusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
        </Field>
        <Field label="Description" htmlFor={`${id}-description`} error={form.formState.errors.description?.message} hint="Markdown supported: headings, lists, emphasis and links.">
          <div className="improvement-editor-tabs" role="tablist" aria-label="Description editor">
            {(['Write', 'Preview'] as const).map((label, index) => <button key={label} type="button" role="tab" id={`${id}-tab-${index}`} aria-controls={`${id}-panel-${index}`} aria-selected={preview === Boolean(index)}
              tabIndex={preview === Boolean(index) ? 0 : -1} onClick={() => setPreview(Boolean(index))}
              onKeyDown={event => {
                if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
                  event.preventDefault();
                  const next = event.key === 'Home' ? false : event.key === 'End' ? true : !preview;
                  setPreview(next); document.getElementById(`${id}-tab-${Number(next)}`)?.focus();
                }
              }}>{label}</button>)}
          </div>
          <div role="tabpanel" id={`${id}-panel-0`} aria-labelledby={`${id}-tab-0`} hidden={preview}>
            <textarea id={`${id}-description`} rows={10} maxLength={20000} {...form.register('description')} />
          </div>
          <div role="tabpanel" id={`${id}-panel-1`} aria-labelledby={`${id}-tab-1`} hidden={!preview} className="improvement-preview"><ImprovementMarkdown>{description}</ImprovementMarkdown></div>
        </Field>
      </fieldset>
      {save.error && <ErrorNotice error={save.error} />}
      <div className="form-actions"><Button type="button" variant="outline" disabled={save.isPending} onClick={close}>Cancel</Button><Button type="submit" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save improvement'}</Button></div>
    </form>
  </Dialog>;
}
